<script setup lang="ts">
/**
 * AppSidebar - ChatGPT 风格左侧栏.
 *
 * 桌面 (lg+): 常驻 static 块, 可折叠 (只留图标栏).
 * 移动 (<lg): fixed 抽屉, 由 AppLayout 经 `open` prop 控制.
 *
 * 结构:
 *   1. 头部: 折叠按钮 + Logo + 移动端关闭
 *   2. 可滚动中部:
 *      - 军师列表 (角色卡 -> 跳介绍页)
 *      - 数字员工 (可展开: 制作图片/视频/音频)
 *      - 收藏夹 (按分组展示, 可建组/选组/重命名/删除)
 *      - 历史对话 (全部列表, 可重命名/收藏到分组/删除)
 *   3. 底部账号
 */
import { computed, onMounted, reactive, ref, watch } from 'vue'
import { RouterLink, useRoute, useRouter } from 'vue-router'
import {
  ArrowRightOnRectangleIcon,
  Bars3Icon,
  ChatBubbleLeftRightIcon,
  ChevronDownIcon,
  ClockIcon,
  FolderPlusIcon,
  MusicalNoteIcon,
  PencilIcon,
  PhotoIcon,
  Squares2X2Icon,
  StarIcon as StarOutlineIcon,
  TrashIcon,
  VideoCameraIcon,
  XMarkIcon,
} from '@heroicons/vue/24/outline'
import { StarIcon as StarSolidIcon } from '@heroicons/vue/24/solid'
import {
  deleteConversation,
  listConversations,
  renameConversation,
  toggleConversationStar,
  unstarConversation,
} from '@/api/conversations'
import {
  createStarGroup,
  deleteStarGroup,
  listStarGroups,
  updateStarGroup,
} from '@/api/star-groups'
import { extractErrorMessage } from '@/api/client'
import { useChatStore } from '@/stores/chat'
import { useAuthStore } from '@/stores/auth'
import { avatarText } from '@/utils/format'
import ConvItem from '@/components/sidebar/ConvItem.vue'
import RoleAvatar from '@/components/common/RoleAvatar.vue'
import type { Conversation, Role, StarGroup, UUID } from '@/types/api'

defineProps<{ open: boolean }>()
const emit = defineEmits<{ (e: 'close'): void }>()

const router = useRouter()
const route = useRoute()
const chatStore = useChatStore()
const auth = useAuthStore()

// ===== 侧栏折叠 (桌面) =====
const collapsed = ref(false)

// ===== 角色 =====
const roles = computed<Role[]>(() => chatStore.roles)
const loadingRoles = ref(true)
const rolesError = ref('')

// ===== 对话列表 =====
const conversations = ref<Conversation[]>([])
const starredConversations = ref<Conversation[]>([])
const loadingConversations = ref(true)
const loadingStarred = ref(true)
const deletingId = ref<string | null>(null)
const togglingId = ref<string | null>(null)

// 实时响应对话标题更新 (后端自动取名后 SSE title 事件 -> chatStore -> 此处)
watch(
  () => chatStore.convTitleUpdates,
  (updates) => {
    if (!updates.length) return
    for (const u of updates) {
      const c = conversations.value.find((x) => x.id === u.id)
      if (c) c.title = u.title
      const sc = starredConversations.value.find((x) => x.id === u.id)
      if (sc) sc.title = u.title
    }
    // 清空已消费的更新, 避免下次重复处理
    chatStore.convTitleUpdates.splice(0, chatStore.convTitleUpdates.length)
  },
  { deep: true },
)

// ===== 收藏分组 =====
const starGroups = ref<StarGroup[]>([])
const loadingGroups = ref(true)
const groupExpanded = ref<Record<string, boolean>>({})
const starPicker = reactive<{
  visible: boolean
  convId: string | null
}>({ visible: false, convId: null })
const createGroupDialog = reactive<{ visible: boolean; name: string }>({
  visible: false,
  name: '',
})
const renameDialog = reactive<{
  visible: boolean
  convId: string | null
  title: string
}>({ visible: false, convId: null, title: '' })
const renameGroupDialog = reactive<{
  visible: boolean
  groupId: string | null
  name: string
}>({ visible: false, groupId: null, name: '' })

const roleMap = computed<Map<string, Role>>(() => {
  const m = new Map<string, Role>()
  for (const r of roles.value) m.set(r.id, r)
  return m
})

const isStudio = computed<boolean>(() => route.path.startsWith('/studio'))
const isMe = computed<boolean>(() => route.path.startsWith('/me'))
const activeRoleId = computed<string>(() =>
  route.name === 'role-intro' ? String(route.params.id ?? '') : '',
)

// ===== 数字员工子菜单 =====
interface StudioSubItem {
  key: string
  label: string
  icon: typeof PhotoIcon
  query: string
}
const studioSubItems: StudioSubItem[] = [
  { key: 'image', label: '制作图片', icon: PhotoIcon, query: '?tab=image' },
  { key: 'video', label: '制作视频', icon: VideoCameraIcon, query: '?tab=video' },
  { key: 'music', label: '制作音频', icon: MusicalNoteIcon, query: '?tab=music' },
]
const studioGroupOpen = ref<boolean>(true)
const rolesGroupOpen = ref<boolean>(true)
const starredGroupOpen = ref<boolean>(true)
const historyGroupOpen = ref<boolean>(true)
const activeStudioTab = computed<string>(() => String(route.query.tab ?? 'image'))
function isStudioSubActive(item: StudioSubItem): boolean {
  return isStudio.value && activeStudioTab.value === item.key
}
function toggleStudioGroup(): void {
  studioGroupOpen.value = !studioGroupOpen.value
}

function roleOf(conv: Conversation): Role | undefined {
  return roleMap.value.get(conv.role_id ?? '')
}

/** 历史对话: 过滤掉已收藏的 (收藏的只在收藏夹显示). */
const unstarredConversations = computed<Conversation[]>(() =>
  conversations.value.filter((c) => !c.starred),
)

// ===== 收藏夹: 按分组归类 =====
const ungroupedStarred = computed<Conversation[]>(() =>
  starredConversations.value.filter((c) => !c.star_group_id),
)
function starredOfGroup(groupId: UUID): Conversation[] {
  return starredConversations.value.filter((c) => c.star_group_id === groupId)
}
function toggleGroupExpand(groupId: string): void {
  groupExpanded.value[groupId] = !groupExpanded.value[groupId]
}
function isGroupExpanded(groupId: string): boolean {
  return groupExpanded.value[groupId] !== false
}

// ===== 数据加载 =====
async function loadRoles(): Promise<void> {
  loadingRoles.value = true
  rolesError.value = ''
  try {
    await chatStore.ensureRoles()
  } catch (e) {
    rolesError.value = extractErrorMessage(e, '角色加载失败')
  } finally {
    loadingRoles.value = false
  }
}

async function loadConversations(): Promise<void> {
  loadingConversations.value = true
  try {
    conversations.value = await listConversations()
  } catch {
    conversations.value = []
  } finally {
    loadingConversations.value = false
  }
}

async function loadStarred(): Promise<void> {
  loadingStarred.value = true
  try {
    starredConversations.value = await listConversations(undefined, true)
  } catch {
    starredConversations.value = []
  } finally {
    loadingStarred.value = false
  }
}

async function loadStarGroups(): Promise<void> {
  loadingGroups.value = true
  try {
    starGroups.value = await listStarGroups()
  } catch {
    starGroups.value = []
  } finally {
    loadingGroups.value = false
  }
}

// ===== 导航 =====
function pickRole(role: Role): void {
  void router.push({ name: 'role-intro', params: { id: role.id } })
  emit('close')
}

function openConversation(conv: Conversation): void {
  chatStore.setCurrentConversation(conv.id)
  // Studio 类型对话 -> 跳数字员工对应 tab, 带 conv 加载历史
  if (conv.type.startsWith('studio_')) {
    const tab = conv.type.replace('studio_', '')
    void router.push({ name: 'studio', query: { tab, conv: conv.id } })
    emit('close')
    return
  }
  void router.push({
    name: 'conversation',
    params: { id: conv.id },
    query: conv.role_id ? { role_id: conv.role_id } : undefined,
  })
  emit('close')
}

// ===== 收藏 =====
function onStarClick(conv: Conversation, e: Event): void {
  e.stopPropagation()
  if (conv.starred) {
    void doUnstar(conv)
  } else {
    starPicker.visible = true
    starPicker.convId = conv.id
  }
}

async function doStar(groupId: UUID | null): Promise<void> {
  const convId = starPicker.convId
  if (!convId) return
  starPicker.visible = false
  starPicker.convId = null
  const conv = conversations.value.find((c) => c.id === convId)
  if (!conv) return
  togglingId.value = convId
  const updated = { ...conv, starred: true, star_group_id: groupId }
  conversations.value = conversations.value.map((c) => (c.id === convId ? updated : c))
  starredConversations.value = [updated, ...starredConversations.value]
  try {
    const res = await toggleConversationStar(convId, groupId)
    conversations.value = conversations.value.map((c) => (c.id === convId ? res : c))
    starredConversations.value = starredConversations.value.map((c) =>
      c.id === convId ? res : c,
    )
  } catch {
    await Promise.all([loadConversations(), loadStarred()])
  } finally {
    togglingId.value = null
  }
}

async function doUnstar(conv: Conversation): Promise<void> {
  togglingId.value = conv.id
  conversations.value = conversations.value.map((c) =>
    c.id === conv.id ? { ...c, starred: false, star_group_id: null } : c,
  )
  starredConversations.value = starredConversations.value.filter((c) => c.id !== conv.id)
  try {
    await unstarConversation(conv.id)
  } catch {
    await Promise.all([loadConversations(), loadStarred()])
  } finally {
    togglingId.value = null
  }
}

// ===== 重命名对话 =====
function onRenameClick(conv: Conversation, e: Event): void {
  e.stopPropagation()
  renameDialog.visible = true
  renameDialog.convId = conv.id
  renameDialog.title = conv.title || '新对话'
}

async function doRename(): Promise<void> {
  const convId = renameDialog.convId
  const title = renameDialog.title.trim()
  if (!convId || !title) return
  renameDialog.visible = false
  try {
    const res = await renameConversation(convId, title)
    conversations.value = conversations.value.map((c) => (c.id === convId ? res : c))
    starredConversations.value = starredConversations.value.map((c) =>
      c.id === convId ? res : c,
    )
  } catch {
    // 静默
  }
}

// ===== 删除 =====
async function removeConversation(conv: Conversation, e: Event): Promise<void> {
  e.stopPropagation()
  confirmDialog.visible = true
  confirmDialog.title = conv.title || '新对话'
  await new Promise<void>((resolve) => {
    confirmDialog.resolve = resolve
  })
  if (!confirmDialog.confirmed) return
  deletingId.value = conv.id
  try {
    await deleteConversation(conv.id)
    conversations.value = conversations.value.filter((c) => c.id !== conv.id)
    starredConversations.value = starredConversations.value.filter((c) => c.id !== conv.id)
  } catch {
    // 静默
  } finally {
    deletingId.value = null
  }
}

// ===== 收藏分组管理 =====
function openCreateGroup(): void {
  createGroupDialog.visible = true
  createGroupDialog.name = ''
}

async function doCreateGroup(): Promise<void> {
  const name = createGroupDialog.name.trim()
  if (!name) return
  createGroupDialog.visible = false
  try {
    const g = await createStarGroup({ name, sort_order: starGroups.value.length })
    starGroups.value = [...starGroups.value, g]
  } catch {
    // 静默
  }
}

function onRenameGroupClick(group: StarGroup, e: Event): void {
  e.stopPropagation()
  renameGroupDialog.visible = true
  renameGroupDialog.groupId = group.id
  renameGroupDialog.name = group.name
}

async function doRenameGroup(): Promise<void> {
  const groupId = renameGroupDialog.groupId
  const name = renameGroupDialog.name.trim()
  if (!groupId || !name) return
  renameGroupDialog.visible = false
  try {
    const res = await updateStarGroup(groupId, { name })
    starGroups.value = starGroups.value.map((g) => (g.id === groupId ? res : g))
  } catch {
    // 静默
  }
}

async function onDeleteGroup(group: StarGroup, e: Event): Promise<void> {
  e.stopPropagation()
  try {
    await deleteStarGroup(group.id)
    starGroups.value = starGroups.value.filter((g) => g.id !== group.id)
    starredConversations.value = starredConversations.value.map((c) =>
      c.star_group_id === group.id ? { ...c, star_group_id: null } : c,
    )
  } catch {
    // 静默
  }
}

// ===== 删除确认对话框 =====
const confirmDialog = reactive<{
  visible: boolean
  title: string
  confirmed: boolean
  resolve: (() => void) | null
}>({ visible: false, title: '', confirmed: false, resolve: null })

function confirmDelete(): void {
  confirmDialog.confirmed = true
  confirmDialog.visible = false
  confirmDialog.resolve?.()
  confirmDialog.resolve = null
}

function cancelDelete(): void {
  confirmDialog.confirmed = false
  confirmDialog.visible = false
  confirmDialog.resolve?.()
  confirmDialog.resolve = null
}

async function onLogout(): Promise<void> {
  await auth.logout()
}

onMounted(() => {
  void Promise.all([loadRoles(), loadConversations(), loadStarred(), loadStarGroups()])
})
</script>

<template>
  <aside
    class="fixed inset-y-0 left-0 z-40 flex w-[280px] flex-col border-r border-gray-200/70 bg-sidebar transition-all duration-300 ease-out lg:static lg:z-auto lg:h-full lg:translate-x-0 lg:transition-none"
    :class="[
      collapsed ? 'lg:w-[60px]' : 'lg:w-[280px]',
      open ? 'translate-x-0 shadow-soft-md' : '-translate-x-full',
    ]"
  >
    <!-- 头部 -->
    <header class="flex shrink-0 items-center justify-between px-3 pb-2 pt-4 pt-safe">
      <span v-if="!collapsed" class="text-[15px] font-semibold tracking-tight text-gray-800">知行AI</span>
      <span v-else class="text-[15px] font-semibold tracking-tight text-gray-800">知</span>
      <div class="flex items-center gap-1">
        <!-- 折叠按钮 (仅桌面) -->
        <button
          type="button"
          class="hidden rounded-full p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 lg:block"
          :aria-label="collapsed ? '展开侧栏' : '收起侧栏'"
          @click="collapsed = !collapsed"
        >
          <Bars3Icon class="h-5 w-5" :aria-hidden="true" />
        </button>
        <!-- 移动端关闭 -->
        <button
          type="button"
          class="rounded-full p-1.5 text-gray-400 active:bg-gray-100 lg:hidden"
          aria-label="关闭侧栏"
          @click="emit('close')"
        >
          <XMarkIcon class="h-5 w-5" :aria-hidden="true" />
        </button>
      </div>
    </header>

    <!-- 可滚动中部 -->
    <div v-show="!collapsed" class="flex-1 overflow-y-auto pb-2">
      <!-- 军师列表 -->
      <section class="px-2 pb-1">
        <button
          type="button"
          class="flex w-full items-center gap-1.5 px-2 pb-1.5 pt-3 text-[13px] font-medium text-gray-600 transition-colors hover:text-gray-800"
          @click="rolesGroupOpen = !rolesGroupOpen"
        >
          <ChevronDownIcon
            class="h-3.5 w-3.5 text-gray-400 transition-transform"
            :class="rolesGroupOpen ? '' : '-rotate-90'"
            :aria-hidden="true"
          />
          <ChatBubbleLeftRightIcon class="h-4 w-4 text-gray-500" :aria-hidden="true" />
          <span>军师列表</span>
        </button>
        <div v-show="rolesGroupOpen">
          <div v-if="loadingRoles" class="px-2 py-2 text-sm text-gray-400">加载中…</div>
          <div
            v-else-if="rolesError"
            class="mx-2 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600"
            role="alert"
          >
            {{ rolesError }}
          </div>
          <div v-else-if="roles.length === 0" class="px-2 py-3 text-sm text-gray-400">
            暂无可用角色
          </div>
          <ul v-else class="flex flex-col gap-0.5">
            <li v-for="role in roles" :key="role.id">
              <button
                type="button"
                class="flex w-full items-center gap-2.5 rounded-lg px-2 py-2 text-left transition-colors hover:bg-sidebar-hover active:scale-[0.98]"
                :class="activeRoleId === role.id ? 'bg-primary/8' : ''"
                @click="pickRole(role)"
              >
                <RoleAvatar :avatar="role.avatar" :name="role.name" :size="32" />
                <span
                  class="line-clamp-1 flex-1 text-sm"
                  :class="activeRoleId === role.id ? 'font-medium text-primary' : 'text-gray-700'"
                >{{ role.name }}</span>
              </button>
            </li>
          </ul>
        </div>
      </section>

      <!-- 数字员工 -->
      <section class="px-2 pb-1">
        <button
          type="button"
          class="flex w-full items-center gap-1.5 px-2 pb-1.5 pt-3 text-[13px] font-medium text-gray-600 transition-colors hover:text-gray-800"
          @click="toggleStudioGroup"
        >
          <ChevronDownIcon
            class="h-3.5 w-3.5 text-gray-400 transition-transform"
            :class="studioGroupOpen ? '' : '-rotate-90'"
            :aria-hidden="true"
          />
          <Squares2X2Icon class="h-4 w-4 text-gray-500" :aria-hidden="true" />
          <span>数字员工</span>
        </button>
        <ul v-show="studioGroupOpen" class="mt-0.5 flex flex-col gap-0.5 pl-1">
          <li v-for="item in studioSubItems" :key="item.key">
            <RouterLink
              :to="`/studio${item.query}`"
              class="flex items-center gap-2.5 rounded-lg px-2 py-1.5 pl-7 text-[13px] transition-colors hover:bg-sidebar-hover"
              :class="isStudioSubActive(item) ? 'bg-primary/8 text-primary' : 'text-gray-500'"
              @click="emit('close')"
            >
              <component :is="item.icon" class="h-4 w-4" :aria-hidden="true" />
              <span class="flex-1">{{ item.label }}</span>
            </RouterLink>
          </li>
        </ul>
      </section>

      <!-- 收藏夹 (按分组) -->
      <section class="px-2 pb-1">
        <div class="flex items-center justify-between px-2 pb-1.5 pt-3">
          <button
            type="button"
            class="flex items-center gap-1.5 text-[13px] font-medium text-gray-600 transition-colors hover:text-gray-800"
            @click="starredGroupOpen = !starredGroupOpen"
          >
            <ChevronDownIcon
              class="h-3.5 w-3.5 text-gray-400 transition-transform"
              :class="starredGroupOpen ? '' : '-rotate-90'"
              :aria-hidden="true"
            />
            <StarOutlineIcon class="h-4 w-4 text-gray-500" :aria-hidden="true" />
            <span>收藏夹</span>
          </button>
          <button
            type="button"
            class="rounded-full p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-primary"
            aria-label="新建分组"
            @click="openCreateGroup"
          >
            <FolderPlusIcon class="h-4 w-4" :aria-hidden="true" />
          </button>
        </div>
        <div v-show="starredGroupOpen">
        <div v-if="loadingStarred" class="px-2 py-2 text-sm text-gray-400">加载中…</div>
        <div
          v-else-if="starredConversations.length === 0 && starGroups.length === 0"
          class="px-2 py-2 text-xs text-gray-400"
        >
          暂无收藏
        </div>
        <template v-else>
          <!-- 未分组 -->
          <div v-if="ungroupedStarred.length > 0" class="mb-1">
            <div
              class="flex cursor-pointer items-center gap-1 px-2 py-1 text-xs font-medium text-gray-400"
              @click="toggleGroupExpand('__ungrouped__')"
            >
              <ChevronDownIcon
                class="h-3 w-3 transition-transform"
                :class="isGroupExpanded('__ungrouped__') ? '' : '-rotate-90'"
                :aria-hidden="true"
              />
              <span>未分组</span>
              <span class="text-gray-300">({{ ungroupedStarred.length }})</span>
            </div>
            <ul v-show="isGroupExpanded('__ungrouped__')" class="flex flex-col gap-0.5">
              <li v-for="conv in ungroupedStarred" :key="conv.id">
                <ConvItem
                  :conv="conv"
                  :role="roleOf(conv)"
                  :active="chatStore.currentConversationId === conv.id"
                  :toggling="togglingId === conv.id"
                  :deleting="deletingId === conv.id"
                  @open="openConversation(conv)"
                  @star="onStarClick(conv, $event)"
                  @rename="onRenameClick(conv, $event)"
                  @delete="removeConversation(conv, $event)"
                />
              </li>
            </ul>
          </div>

          <!-- 各分组 -->
          <div v-for="group in starGroups" :key="group.id" class="mb-1">
            <div
              class="flex cursor-pointer items-center gap-1 px-2 py-1 text-xs font-medium text-gray-400 hover:text-gray-500"
              @click="toggleGroupExpand(group.id)"
            >
              <ChevronDownIcon
                class="h-3 w-3 transition-transform"
                :class="isGroupExpanded(group.id) ? '' : '-rotate-90'"
                :aria-hidden="true"
              />
              <span class="flex-1 truncate">{{ group.name }}</span>
              <span class="text-gray-300">({{ starredOfGroup(group.id).length }})</span>
              <button
                type="button"
                class="ml-1 rounded-full p-0.5 text-gray-300 hover:text-primary"
                aria-label="重命名分组"
                @click="onRenameGroupClick(group, $event)"
              >
                <PencilIcon class="h-3 w-3" :aria-hidden="true" />
              </button>
              <button
                type="button"
                class="rounded-full p-0.5 text-gray-300 hover:text-red-500"
                aria-label="删除分组"
                @click="onDeleteGroup(group, $event)"
              >
                <TrashIcon class="h-3 w-3" :aria-hidden="true" />
              </button>
            </div>
            <ul v-show="isGroupExpanded(group.id)" class="flex flex-col gap-0.5">
              <li v-for="conv in starredOfGroup(group.id)" :key="conv.id">
                <ConvItem
                  :conv="conv"
                  :role="roleOf(conv)"
                  :active="chatStore.currentConversationId === conv.id"
                  :toggling="togglingId === conv.id"
                  :deleting="deletingId === conv.id"
                  @open="openConversation(conv)"
                  @star="onStarClick(conv, $event)"
                  @rename="onRenameClick(conv, $event)"
                  @delete="removeConversation(conv, $event)"
                />
              </li>
            </ul>
          </div>
        </template>
        </div>
      </section>

      <!-- 历史对话 -->
      <section class="px-2 pb-1">
        <button
          type="button"
          class="flex w-full items-center gap-1.5 px-2 pb-1.5 pt-3 text-[13px] font-medium text-gray-600 transition-colors hover:text-gray-800"
          @click="historyGroupOpen = !historyGroupOpen"
        >
          <ChevronDownIcon
            class="h-3.5 w-3.5 text-gray-400 transition-transform"
            :class="historyGroupOpen ? '' : '-rotate-90'"
            :aria-hidden="true"
          />
          <ClockIcon class="h-4 w-4 text-gray-500" :aria-hidden="true" />
          <span>历史对话</span>
        </button>
        <div v-show="historyGroupOpen">
        <div v-if="loadingConversations" class="px-2 py-2 text-sm text-gray-400">加载中…</div>
        <div v-else-if="unstarredConversations.length === 0" class="px-2 py-2 text-xs text-gray-400">
          暂无对话
        </div>
        <ul v-else class="flex flex-col gap-0.5">
          <li v-for="conv in unstarredConversations" :key="conv.id">
            <ConvItem
              :conv="conv"
              :role="roleOf(conv)"
              :active="chatStore.currentConversationId === conv.id"
              :toggling="togglingId === conv.id"
              :deleting="deletingId === conv.id"
              show-star
              @open="openConversation(conv)"
              @star="onStarClick(conv, $event)"
              @rename="onRenameClick(conv, $event)"
              @delete="removeConversation(conv, $event)"
            />
          </li>
        </ul>
        </div>
      </section>
    </div>

    <!-- 底部账号 -->
    <footer class="shrink-0 border-t border-gray-200/70 p-2 pb-safe">
      <RouterLink
        to="/me"
        class="flex items-center gap-2.5 rounded-lg px-2 py-2 transition-colors hover:bg-sidebar-hover"
        :class="isMe ? 'bg-primary/8' : ''"
        @click="emit('close')"
      >
        <span
          class="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-full bg-primary text-sm font-semibold text-white"
        >
          <img
            v-if="auth.user?.avatar"
            :src="auth.user.avatar"
            :alt="auth.displayName"
            class="h-full w-full object-cover"
          />
          <span v-else>{{ avatarText(auth.displayName) }}</span>
        </span>
        <span v-if="!collapsed" class="flex-1 overflow-hidden">
          <span class="block line-clamp-1 text-sm font-medium text-gray-700">{{
            auth.displayName
          }}</span>
          <span class="block text-[11px] text-gray-400">个人中心</span>
        </span>
      </RouterLink>
      <button
        v-if="!collapsed"
        type="button"
        class="mt-1 flex w-full items-center gap-2.5 rounded-lg px-2 py-2 text-sm text-gray-500 transition-colors hover:bg-sidebar-hover"
        @click="onLogout"
      >
        <ArrowRightOnRectangleIcon class="h-5 w-5" :aria-hidden="true" />
        <span>退出登录</span>
      </button>
    </footer>

    <!-- ===== 删除确认对话框 ===== -->
    <Teleport to="body">
      <Transition
        enter-active-class="transition duration-200 ease-out"
        enter-from-class="opacity-0"
        leave-active-class="transition duration-150 ease-in"
        leave-to-class="opacity-0"
      >
        <div
          v-if="confirmDialog.visible"
          class="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 px-6"
          @click="cancelDelete"
        >
          <Transition
            enter-active-class="transition duration-200 ease-out"
            enter-from-class="opacity-0 scale-95"
            leave-active-class="transition duration-150 ease-in"
            leave-to-class="opacity-0 scale-95"
          >
            <div
              v-if="confirmDialog.visible"
              class="w-full max-w-xs rounded-2xl bg-white p-5 shadow-xl"
              @click.stop
            >
              <h3 class="text-base font-semibold text-gray-800">删除对话</h3>
              <p class="mt-2 text-sm text-gray-500">
                确定删除「{{ confirmDialog.title }}」吗？删除后无法恢复。
              </p>
              <div class="mt-5 flex gap-2.5">
                <button
                  type="button"
                  class="flex-1 rounded-xl border border-gray-200 py-2.5 text-sm font-medium text-gray-600 transition-colors active:bg-gray-50"
                  @click="cancelDelete"
                >
                  取消
                </button>
                <button
                  type="button"
                  class="flex-1 rounded-xl bg-red-500 py-2.5 text-sm font-medium text-white transition-colors active:bg-red-600"
                  @click="confirmDelete"
                >
                  删除
                </button>
              </div>
            </div>
          </Transition>
        </div>
      </Transition>
    </Teleport>

    <!-- ===== 收藏选组弹窗 ===== -->
    <Teleport to="body">
      <Transition
        enter-active-class="transition duration-200 ease-out"
        enter-from-class="opacity-0"
        leave-active-class="transition duration-150 ease-in"
        leave-to-class="opacity-0"
      >
        <div
          v-if="starPicker.visible"
          class="fixed inset-0 z-[60] flex items-end justify-center bg-black/40 sm:items-center"
          @click="starPicker.visible = false"
        >
          <div class="w-full max-w-xs rounded-2xl bg-white p-4 shadow-xl" @click.stop>
            <h3 class="mb-3 text-base font-semibold text-gray-800">收藏到分组</h3>
            <button
              type="button"
              class="flex w-full items-center gap-2 rounded-xl px-3 py-2.5 text-sm text-gray-700 transition-colors hover:bg-gray-50"
              @click="doStar(null)"
            >
              <StarOutlineIcon class="h-4 w-4 text-gray-400" :aria-hidden="true" />
              未分组
            </button>
            <button
              v-for="g in starGroups"
              :key="g.id"
              type="button"
              class="flex w-full items-center gap-2 rounded-xl px-3 py-2.5 text-sm text-gray-700 transition-colors hover:bg-gray-50"
              @click="doStar(g.id)"
            >
              <StarSolidIcon class="h-4 w-4 text-amber-400" :aria-hidden="true" />
              {{ g.name }}
            </button>
            <button
              type="button"
              class="mt-2 flex w-full items-center justify-center gap-1.5 rounded-xl border border-dashed border-gray-300 px-3 py-2.5 text-sm text-gray-500 transition-colors hover:border-primary hover:text-primary"
              @click="
                () => {
                  starPicker.visible = false
                  openCreateGroup()
                }
              "
            >
              <FolderPlusIcon class="h-4 w-4" :aria-hidden="true" />
              新建分组
            </button>
          </div>
        </div>
      </Transition>
    </Teleport>

    <!-- ===== 新建分组弹窗 ===== -->
    <Teleport to="body">
      <Transition
        enter-active-class="transition duration-200 ease-out"
        enter-from-class="opacity-0"
        leave-active-class="transition duration-150 ease-in"
        leave-to-class="opacity-0"
      >
        <div
          v-if="createGroupDialog.visible"
          class="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 px-6"
          @click="createGroupDialog.visible = false"
        >
          <div class="w-full max-w-xs rounded-2xl bg-white p-5 shadow-xl" @click.stop>
            <h3 class="text-base font-semibold text-gray-800">新建分组</h3>
            <input
              v-model="createGroupDialog.name"
              type="text"
              maxlength="64"
              placeholder="分组名称"
              class="mt-3 w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
              @keydown.enter="doCreateGroup"
            />
            <div class="mt-4 flex gap-2.5">
              <button
                type="button"
                class="flex-1 rounded-xl border border-gray-200 py-2.5 text-sm font-medium text-gray-600 active:bg-gray-50"
                @click="createGroupDialog.visible = false"
              >
                取消
              </button>
              <button
                type="button"
                class="flex-1 rounded-xl bg-primary py-2.5 text-sm font-medium text-white active:opacity-80"
                @click="doCreateGroup"
              >
                创建
              </button>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>

    <!-- ===== 重命名对话弹窗 ===== -->
    <Teleport to="body">
      <Transition
        enter-active-class="transition duration-200 ease-out"
        enter-from-class="opacity-0"
        leave-active-class="transition duration-150 ease-in"
        leave-to-class="opacity-0"
      >
        <div
          v-if="renameDialog.visible"
          class="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 px-6"
          @click="renameDialog.visible = false"
        >
          <div class="w-full max-w-xs rounded-2xl bg-white p-5 shadow-xl" @click.stop>
            <h3 class="text-base font-semibold text-gray-800">重命名对话</h3>
            <input
              v-model="renameDialog.title"
              type="text"
              maxlength="128"
              placeholder="对话名称"
              class="mt-3 w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
              @keydown.enter="doRename"
            />
            <div class="mt-4 flex gap-2.5">
              <button
                type="button"
                class="flex-1 rounded-xl border border-gray-200 py-2.5 text-sm font-medium text-gray-600 active:bg-gray-50"
                @click="renameDialog.visible = false"
              >
                取消
              </button>
              <button
                type="button"
                class="flex-1 rounded-xl bg-primary py-2.5 text-sm font-medium text-white active:opacity-80"
                @click="doRename"
              >
                保存
              </button>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>

    <!-- ===== 重命名分组弹窗 ===== -->
    <Teleport to="body">
      <Transition
        enter-active-class="transition duration-200 ease-out"
        enter-from-class="opacity-0"
        leave-active-class="transition duration-150 ease-in"
        leave-to-class="opacity-0"
      >
        <div
          v-if="renameGroupDialog.visible"
          class="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 px-6"
          @click="renameGroupDialog.visible = false"
        >
          <div class="w-full max-w-xs rounded-2xl bg-white p-5 shadow-xl" @click.stop>
            <h3 class="text-base font-semibold text-gray-800">重命名分组</h3>
            <input
              v-model="renameGroupDialog.name"
              type="text"
              maxlength="64"
              placeholder="分组名称"
              class="mt-3 w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
              @keydown.enter="doRenameGroup"
            />
            <div class="mt-4 flex gap-2.5">
              <button
                type="button"
                class="flex-1 rounded-xl border border-gray-200 py-2.5 text-sm font-medium text-gray-600 active:bg-gray-50"
                @click="renameGroupDialog.visible = false"
              >
                取消
              </button>
              <button
                type="button"
                class="flex-1 rounded-xl bg-primary py-2.5 text-sm font-medium text-white active:opacity-80"
                @click="doRenameGroup"
              >
                保存
              </button>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>
  </aside>
</template>
